<script lang="ts">
	import { Alert, ButtonV2, IconButton, InputV2, toast, Text } from '@Shelly/shelly-design-system/index';

	import { requestEditDevice } from 'src/components/Lists/list-helpers';
	import { valueOutOfRange } from 'src/helpers/utilities';
	import { format } from 'svelte-i18n';
	import { devices } from 'src/stores/lists/devices';
	import type { Device } from 'src/types/Device';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { bindStickyBottomAction } from 'src/helpers/sticky_bottom_action';

	export let device: Device;

	const min = 0;
	const max = 100;

	let valve_fav_positions: number[] = $devices[device.id]?.valve_fav_positions || [];

	const addFavorite = () => {
		if (valve_fav_positions.length < 4) {
			valve_fav_positions = [...valve_fav_positions, 50];
		}
	};

	const removeFavorite = (index: number) => {
		valve_fav_positions = valve_fav_positions.filter((_, i) => i !== index);
	};

	const saveValveFavPositions = async () => {
		// Convert to numbers and filter out invalid values and duplicates
		const numberPositions = valve_fav_positions.filter((val) => !isNaN(val));

		const validPositions = numberPositions.filter(
			(val, index, self) => !valueOutOfRange(val, min, max) && self.indexOf(val) === index,
		);

		const res = await requestEditDevice({
			...device,
			valve_fav_positions: validPositions,
		});

		if (res?.isok) {
			toast.open({
				type: 'success',
				title: $format('toast_success_fav_valve_positions', {
					default: 'Successfully saved favorite valve positions',
				}),
			});
		}
	};

	const hasErrors = () => {
		const numberPositions = valve_fav_positions.filter((val) => !isNaN(val));
		return numberPositions.some((val) => valueOutOfRange(val, min, max));
	};

	$: saveDisabled = hasErrors() || valve_fav_positions.length === 0;

	const { stickyBottomAction, setStickyBottomAction } = bindStickyBottomAction();

	$: if (stickyBottomAction) {
		if ($showNewLayout) {
			setStickyBottomAction({
				onClick: saveValveFavPositions,
				loading: false,
				disabled: saveDisabled,
				icon: 'fas fa-save',
			});
		} else {
			setStickyBottomAction(null);
		}
	}
</script>

<Alert
	type="info"
	class="mb-m"
	heading={$format('alert_info_heading_unit_valve_fav_positions', {
		default:
			'Set favorite valve opening positions (0-100%). These will appear as quick-access buttons on the slider in manual mode.',
	})}
/>

{#if valve_fav_positions.length === 0}
	<Alert
		type="info"
		class="mb-s"
		heading={$format('alert_info_no_valve_favorites', {
			default: 'No favorite positions yet',
		})}
		message={$format('alert_info_message_no_valve_favorites', {
			default:
				'Click "Add favorite" to create quick-access positions, or use the star button on the slider.',
		})}
	/>
{/if}

{#each valve_fav_positions as _, i (i)}
	{@const currentValue = valve_fav_positions[i]}
	<Text styleType="body-small" class="mb-xs">
		{$format('label_favorite_position_number', {
			values: { number: i + 1 },
			default: `Favorite position ${i + 1}`,
		})}
	</Text>
	<div class="mb-s d-flex align-items-start">
		<InputV2
			aria-label={$format('input_set_favorite_valve_position', {
				default: 'Input set favorite valve position',
			})}
			size="m"
			class="mr-s flex-1"
			type="number"
			icon="fas fa-star"
			{min}
			{max}
			value={currentValue.toString()}
			on:blur={(e) => {
				const val = parseFloat(e.target['value']);
				if (!isNaN(val)) {
					valve_fav_positions[i] = val;
					valve_fav_positions = valve_fav_positions;
				}
			}}
			error={valueOutOfRange(currentValue, min, max)
				? $format('min_max_error_description', {
						values: { min, max },
						default: 'Value must be between {min} and {max}.',
					})
				: ''}
			label={$format('label_favorite_position_number', {
				values: { number: i + 1 },
				default: 'Favorite position #{number}',
			})}
			id={`input-valve-fav-${i}`}
		/>
		<IconButton
			aria-label={$format('button_delete_favorite_position', {
				default: 'Delete favorite position',
			})}
			icon="fas fa-trash"
			variant="danger"
			size="m"
			on:click={() => removeFavorite(i)}
		/>
	</div>
{/each}

{#if valve_fav_positions.length < 4}
	<ButtonV2
		variant="secondary"
		size="m"
		width="full"
		class="mb-s mt-s"
		icon="fas fa-plus"
		on:click={addFavorite}
	>
		{$format('button_add_favorite', { default: 'Add favorite' })}
	</ButtonV2>
{/if}

{#if !$showNewLayout}
	<ButtonV2
		disabled={saveDisabled}
		class="mt-s"
		size="m"
		width="full"
		on:click={saveValveFavPositions}
	>
		{$format('button_save', { default: 'Save' })}
	</ButtonV2>
{/if}
